import { BarChart } from '@mui/x-charts/BarChart';
import { axisClasses } from '@mui/x-charts/ChartsAxis';
import type { ChartsItemContentProps } from '@mui/x-charts/ChartsTooltip';
import type { AxisConfig } from '@mui/x-charts/models';
import type { GetCustomerCommitmentUsageResponse } from '@weft/api-definition';
import { extract } from '@weft/utils';
import { formatNumberAbbreviated } from 'apps/app/src/utils/number';
import { breakStringWithNewLines } from 'apps/app/src/utils/string';
import React from 'react';
import { CustomerCommitmentUsageChartToolTip } from './customer-commitment-usage-chart-tooltip';

const CHART_SERIES_IDS = Object.freeze({
  Commitment: 'commitment',
  Usage: 'usage',
  Overage: 'overage',
});

const CHART_USAGE_OVERAGE_GROUP_ID = `${CHART_SERIES_IDS.Usage}-${CHART_SERIES_IDS.Overage}`;

type CustomerCommitmentUsageChartProps = {
  customersCommitmentUsage: GetCustomerCommitmentUsageResponse['customerCommitmentUsage']
};

export const CustomerCommitmentUsageChart: React.FC<CustomerCommitmentUsageChartProps> = ({ customersCommitmentUsage }) => {
  const customerNames = extract(customersCommitmentUsage, 'customerName');

  return (
    <BarChart
      height={430}
      layout="horizontal"
      topAxis={{}}
      bottomAxis={null}
      margin={{ left: 125, right: 20 }}
      grid={{ vertical: true }}
      slotProps={{ legend: { hidden: true }, bar: { clipPath: 'inset(0px 1px 0px 0px)' } }}
      tooltip={{
        trigger: 'item', itemContent: (props) => (
          <CustomerCommitmentUsageChartToolTip
            customerNames={customerNames}
            {...props as ChartsItemContentProps<'bar'>}
          />
        ), 
      }}
      sx={{
        [`.${axisClasses.root}`]: {
          [`.${axisClasses.tick}, .${axisClasses.line}`]: {
            stroke: 'none',
          },
        },
      }}
      yAxis={[{
        scaleType: 'band',
        data: customersCommitmentUsage.map(({ customerName }) => breakStringWithNewLines(customerName, 10)),
        tickLabelStyle: {
          fill: '#0D1B27DE',
          fontFamily: 'OakesGrotesk',
          fontWeight: 400,
          fontSize: '12',
        },
        barGapRatio: 0.2,
        categoryGapRatio: 0.3,
      } as AxisConfig<'band'>]}
      xAxis={[{
        tickNumber: 10,
        tickLabelInterval: (_, index) => index % 2 === 0,
        tickLabelStyle: {
          fill: '#1B3D5599',
        },
        valueFormatter: (value) => formatNumberAbbreviated(value as number),
      }]}
      borderRadius={4}
      series={[
        {
          data: extract(customersCommitmentUsage, 'commitmentUnits'),
          color: '#ECEDEE',
          id: CHART_SERIES_IDS.Commitment,
          label: 'Commitment',
        },
        {
          data: customersCommitmentUsage.map(({ commitmentUnits, usageUnits }) => getUsage(commitmentUnits, usageUnits)),
          color: '#B6BABE',
          stack: CHART_USAGE_OVERAGE_GROUP_ID,
          id: CHART_SERIES_IDS.Usage,
          label: 'Usage',
        },
        {
          data: customersCommitmentUsage.map(({ commitmentUnits, usageUnits }) => getOverage(commitmentUnits, usageUnits)),
          color: '#EF5350',
          stack: CHART_USAGE_OVERAGE_GROUP_ID,
          id: CHART_SERIES_IDS.Overage,
          label: 'Overage',
        },
      ]}
    />
  );
};

function getUsage(commitmentUnits: number, usageUnits: number) {
  return Math.min(commitmentUnits, usageUnits);
}

function getOverage(commitmentUnits: number, usageUnits: number) {
  return Math.max(0, usageUnits - commitmentUnits);
}